/**
 * « Tickets over time » — tickets ouverts, fermés et encore ouverts, puis le
 * délai moyen de fermeture dans un second graphique.
 *
 * Deux graphiques et non un double axe (cf. README, « Pourquoi il n'y a aucun
 * graphique à double axe ») : des tickets et des jours n'ont pas d'échelle
 * commune, et deux axes laissent croire à un croisement qui n'existe pas.
 *
 * Par semaine au-delà de deux mois, par tranche de 30 jours au-delà d'un an.
 * Le délai d'un jour porte sur les tickets CRÉÉS ce jour-là et déjà fermés ;
 * celui d'une tranche est pondéré par le nombre de tickets fermés de chaque
 * jour, une approximation tant que tous ne le sont pas.
 */

import type { SavDailyPoint } from '@shared/types/sav';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatDayLong, formatDayShort, formatDecimal, formatInteger } from '../lib/format';
import { bucketSizeFor } from '../lib/sales';
import { AXIS_STYLE, BAR_MAX_SIZE, BAR_RADIUS, CATEGORICAL_COLORS, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';

const OPENED_COLOR = CATEGORICAL_COLORS[1];
const STILL_OPEN_COLOR = CATEGORICAL_COLORS[0];
const CLOSED_COLOR = CATEGORICAL_COLORS[2];
const DELAY_COLOR = CATEGORICAL_COLORS[3];

interface Bucket {
  date: string;
  opened: number;
  closed: number;
  stillOpen: number;
  avgCloseDays: number | null;
}

function bucketLabel(date: string, bucketSize: number): string {
  if (bucketSize === 1) return formatDayLong(date);
  return `${bucketSize === 7 ? 'Semaine' : '30 jours'} à partir du ${formatDayLong(date)}`;
}

const days = (value: number) => `${formatDecimal(value)} j`;

function useBuckets(daily: readonly SavDailyPoint[]) {
  const bucketSize = bucketSizeFor(daily.length);
  const buckets = useMemo<Bucket[]>(() => {
    const out: Bucket[] = [];
    for (let start = 0; start < daily.length; start += bucketSize) {
      const slice = daily.slice(start, start + bucketSize);
      const first = slice[0];
      if (!first) continue;
      const closed = slice.reduce((sum, day) => sum + day.closed, 0);
      const weighted = slice.reduce((sum, day) => sum + (day.avgCloseDays ?? 0) * day.closed, 0);
      out.push({
        date: first.date,
        opened: slice.reduce((sum, day) => sum + day.opened, 0),
        closed,
        stillOpen: slice.reduce((sum, day) => sum + day.stillOpen, 0),
        avgCloseDays: closed > 0 ? Math.round((weighted / closed) * 100) / 100 : null,
      });
    }
    return out;
  }, [daily, bucketSize]);
  return { buckets, bucketSize };
}

function TicketsTooltip({ active, payload, bucketSize }: { active?: boolean; payload?: Array<{ payload?: Bucket }>; bucketSize: number }) {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;
  const rows = [
    { label: 'Tickets ouverts', color: OPENED_COLOR, value: formatInteger(bucket.opened) },
    { label: 'Encore ouverts', color: STILL_OPEN_COLOR, value: formatInteger(bucket.stillOpen) },
    { label: 'Tickets fermés', color: CLOSED_COLOR, value: formatInteger(bucket.closed) },
    { label: 'Délai moyen', color: DELAY_COLOR, value: bucket.avgCloseDays !== null ? days(bucket.avgCloseDays) : '—' },
  ];
  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{bucketLabel(bucket.date, bucketSize)}</p>
      {rows.map((row) => (
        <div className="tooltip__row" key={row.label}>
          <span className="legend__key" style={{ background: row.color }} aria-hidden="true" />
          <span className="tooltip__name">{row.label}</span>
          <span className="tooltip__value">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };

export function SavTicketsChart({ daily }: { daily: readonly SavDailyPoint[] }) {
  const { buckets, bucketSize } = useBuckets(daily);
  const granularity = bucketSize === 1 ? 'par jour' : bucketSize === 7 ? 'par semaine' : 'par tranche de 30 jours';
  const hasData = buckets.some((bucket) => bucket.opened > 0 || bucket.closed > 0);

  const columns: ReadonlyArray<DataColumn<Bucket>> = [
    { key: 'date', header: bucketSize === 1 ? 'Jour' : 'Tranche', render: (row) => bucketLabel(row.date, bucketSize) },
    { key: 'opened', header: 'Ouverts', numeric: true, render: (row) => formatInteger(row.opened) },
    { key: 'closed', header: 'Fermés', numeric: true, render: (row) => formatInteger(row.closed) },
    { key: 'stillOpen', header: 'Encore ouverts', numeric: true, render: (row) => formatInteger(row.stillOpen) },
    { key: 'avgCloseDays', header: 'Délai moyen', numeric: true, render: (row) => (row.avgCloseDays !== null ? days(row.avgCloseDays) : '—') },
  ];

  return (
    <>
      <ChartCard
        title="Tickets over time"
        subtitle={`Tickets ouverts, encore ouverts aujourd'hui, et fermés, ${granularity}`}
        className="chart-grid__wide"
        rows={hasData ? buckets : []}
        columns={columns}
        emptyMessage="Aucun ticket sur cette période."
        chartHeight={300}
      >
        <ChartLegend
          entries={[
            { label: 'Tickets ouverts', color: OPENED_COLOR },
            { label: 'Encore ouverts', color: STILL_OPEN_COLOR },
            { label: 'Tickets fermés', color: CLOSED_COLOR },
          ]}
        />
        <ResponsiveContainer width="100%" height="86%">
          <BarChart data={buckets} margin={{ top: 8, right: 12, bottom: 0, left: 0 }} barGap={2}>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
            <YAxis tickFormatter={formatInteger} tick={axisTick} axisLine={false} tickLine={false} width={48} allowDecimals={false} />
            <Tooltip cursor={{ fill: COLORS.grid, opacity: 0.4 }} content={<TicketsTooltip bucketSize={bucketSize} />} />
            <Bar dataKey="opened" name="Tickets ouverts" fill={OPENED_COLOR} radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]} maxBarSize={BAR_MAX_SIZE} isAnimationActive={false} />
            <Bar dataKey="stillOpen" name="Encore ouverts" fill={STILL_OPEN_COLOR} radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]} maxBarSize={BAR_MAX_SIZE} isAnimationActive={false} />
            <Bar dataKey="closed" name="Tickets fermés" fill={CLOSED_COLOR} radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]} maxBarSize={BAR_MAX_SIZE} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Temps moyen de fermeture"
        subtitle={`Délai création → fermeture des tickets créés ce jour-là et déjà fermés, en jours, ${granularity}`}
        className="chart-grid__wide"
        rows={hasData ? buckets : []}
        columns={columns}
        emptyMessage="Aucun ticket fermé sur cette période."
        chartHeight={220}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={buckets} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
            <YAxis tickFormatter={(value: number) => days(value)} tick={axisTick} axisLine={false} tickLine={false} width={48} />
            <Tooltip cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }} content={<TicketsTooltip bucketSize={bucketSize} />} />
            {/* Pas de ligne à travers un jour sans fermeture : le trou est une information. */}
            <Line
              type="monotone"
              dataKey="avgCloseDays"
              name="Délai moyen"
              stroke={DELAY_COLOR}
              strokeWidth={LINE_WIDTH}
              connectNulls={false}
              dot={buckets.length <= 62 ? { r: 3, fill: DELAY_COLOR, stroke: COLORS.surface, strokeWidth: 2 } : false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </>
  );
}
